import { atom, DefaultValue, selector } from 'recoil';
import { UserPreferences, userPreferencesService } from '../../services/userPreferencesService';
import { authVerifiedState, currentUserState } from '../auth/authState';

const _shadowUserPreferences = atom<UserPreferences | undefined>({
  key: 'sd:shadowUserPreferences',
  default: undefined
});

/**
 * Gets or sets the user's preferences and syncs
 * the preferences with the SD Rails backend
 */
export const userPreferencesState = selector<UserPreferences | undefined>({
  key: 'sd:userPreferences',
  get: o => {
    const authVerified = o.get(authVerifiedState);
    if (!authVerified) return;
    const contactGuid = o.get(currentUserState)?.contactGuid;
    const pref = o.get(_shadowUserPreferences);
    if (pref && pref.contactGuid === contactGuid) {
      return pref;
    }

    return contactGuid ? userPreferencesService.get(contactGuid) : undefined;
  },
  set: (o, newValue) => {
    const contactGuid = o.get(currentUserState)?.contactGuid;
    if (!newValue) throw new Error('Attempted to set userPreferences to undefined');
    if (!contactGuid) throw new Error('Attempted to update user preferences but no user is logged in');
    if (newValue instanceof DefaultValue) return;
    if (o.get(_shadowUserPreferences) === newValue) return;
    o.set(_shadowUserPreferences, newValue);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    userPreferencesService.update(contactGuid, newValue);
  }
});

/**
 * Gets or sets the user's prefered number of results to show on LM
 * value is read from and written to SD Rails backend
 */
export const userPrefPerPageState = selector<number>({
  key: 'sd:userPreferences:perPageState',
  get: ({ get }) => {
    const userPrefs = get(userPreferencesState);
    return userPrefs?.vehiclesPerPage || 10;
  },
  set: ({ set, get }, newValue) => {
    const userPrefs = get(userPreferencesState);
    if (newValue instanceof DefaultValue) return;
    if (userPrefs === undefined) return;
    if (userPrefs.vehiclesPerPage === newValue) return;
    set(userPreferencesState, { ...userPrefs, vehiclesPerPage: newValue });
  }
});
